import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  getAiAssistantMessages,
  getAiAssistantMessagesNextPage
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import IsLoadingIndicator from '../../../isLoadingIndicator';
import { MAIN_QUICK_ACTIONS, QUICK_ACTIONS } from './constants';
import { AiAssistantMessageSkeleton } from '../loaders/aiAssistentConversationSkeleton';
import { AiResponse, MyResponse } from './MessageResponseElements';
import { MessageBox } from './aiMessageBox';
import './chatStyle.css';
import MainQuickActions from './mainQuickActions';
import { useTranslate } from '@tolgee/react';
import LomavisAiAssistantUpgradeHint from './upgradeHint';

export const AiConversationColumn: React.FC<any> = () => {
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const scrollableContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dispatch(getAiAssistantMessages() as any);
  }, [dispatch]);

  const getAIprompt = (message: any) => {
    if (message.prompt) {
      return message.prompt;
    }
    if (message.quick_action) {
      return (
        QUICK_ACTIONS.find((action) => action.id === message.quick_action)
          ?.name ||
        MAIN_QUICK_ACTIONS.find((action) => action.id === message.quick_action)
          ?.name
      );
    }
    return null;
  };

  const {
    aiAssistant: {
      conversation: {
        messages,
        pendingMessages,
        count,
        nextPage,
        isInitiated,
        isWaitingForResponse
      },
      aIQuota: { available_tokens, used_tokens }
    }
  } = useSelector((state: any) => state.lomavisCreator);

  useEffect(() => {
    if (scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTop =
        (messages.length + pendingMessages.length) * 1200;
    }
  }, [messages, pendingMessages, isWaitingForResponse]);

  return (
    <div className="card h-100 d-flex flex-column">
      <div
        className="card p-5 flex-grow-1 overflow-auto"
        ref={scrollableContainerRef}
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        <InfiniteScroll
          dataLength={messages.length + pendingMessages.length}
          next={() => dispatch(getAiAssistantMessagesNextPage() as any)}
          hasMore={!!nextPage}
          loader={<IsLoadingIndicator />}
          // For typical "chat" behavior, set 'inverse' to false
          // so that new messages add to the bottom
          inverse={false}
          scrollableTarget="scrollableDiv" // or remove this if you're using the ref
        >
          {[...messages, ...pendingMessages].length === 0 && (
            <p className="text-muted text-center w-100">
              {t('lomavisCreator.mainAcions.helpderText')}
            </p>
          )}

          {/* Show skeletons if the conversation isn't loaded yet */}
          {!isInitiated && (
            <>
              {[...Array(10)].map((_, i) => (
                <AiAssistantMessageSkeleton
                  key={i}
                  side={i % 2 === 0 ? 'left' : 'right'}
                />
              ))}
            </>
          )}

          {/* Render messages in chronological order (oldest at top, newest at bottom) */}
          {messages
            .slice()
            .reverse()
            .map((message: any, index: number) => (
              <div className="mb-5" key={index}>
                {message.created_by_ai ? (
                  <AiResponse message={message} />
                ) : (
                  <MyResponse message={message} getAIprompt={getAIprompt} />
                )}
              </div>
            ))}

          {/* Pending messages (e.g., user messages not yet responded to) at the very bottom */}
          {pendingMessages.map((message: any, index: number) => (
            <MyResponse
              key={`pending-${index}`}
              message={message}
              getAIprompt={(msg) => msg.message}
            />
          ))}

          {/* If AI is generating a response, show typing indicator at the bottom */}
          {Boolean(isWaitingForResponse) && (
            <div className="bg-light p-2 rounded mb-2 w-60px">
              <div className="loader text-secondary" />
            </div>
          )}
        </InfiniteScroll>
      </div>

      {/* if no messages show mainActions component */}
      {[...messages, ...pendingMessages].length === 0 && (
        <div className={'px-3'}>
          <MainQuickActions />
        </div>
      )}

      {/* Input box at the very bottom */}
      {available_tokens !== null && available_tokens - used_tokens <= 0 && (
        <div className={'m-5 mb-0'}>
          <LomavisAiAssistantUpgradeHint
            text={'lomavisCreator.aiAssistant.noQuota'}
          />
        </div>
      )}

      <MessageBox />
    </div>
  );
};
